import React from 'react';
import { styled } from '@mui/material/styles';
import {
  ErrorQuestionDto,
  QuestionResponseDto,
  SectionResponseDto,
  SectionResponseUpdatedDto,
  SubSectionDto,
  SubSectionResponseDto,
} from 'models/dtoTypes';
import { get } from 'lodash';
import { AxiosResponse } from 'axios';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UpdateSubSectionDialog from 'dialogs/UpdateSubSectionDialog';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SubQuestionnaire';

const classes = {
  root: `${PREFIX}-root`,
  label: `${PREFIX}-label`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {},
  [`& .${classes.label}`]: {
    marginBottom: 15,
  },
}));

interface OnSubmitSubSectionResponse {
  (
    questionAlias: string,
    questionResponses: QuestionResponseDto[],
    subSectionId?: string,
  ): Promise<AxiosResponse<SectionResponseDto | ErrorQuestionDto> | void>;
}

interface OnDeleteFile {
  (fileId: string): void;
}

interface OnDeleteSubSectionResponse {
  (subSuctionAlias: string, subSectionResponseId: string): void;
}

interface OnUpdateSection {
  (sectionResponse: SectionResponseUpdatedDto): void;
}

interface Props {
  sectionAlias?: string;
  onSubmitSubSectionResponse?: OnSubmitSubSectionResponse;
  onDeleteSubSectionResponse?: OnDeleteSubSectionResponse;
  subSectionResponses?: SubSectionResponseDto[];
  onUpdateSection?: OnUpdateSection;
  itemsMax?: number;
  itemsMin?: number;
  name: string;
  label?: string;
  subSection?: SubSectionDto;
  onDeleteFile: OnDeleteFile;
}

function SubQuestionnaireForDirectorOrShareholder(props: Props): JSX.Element {
  const { itemsMax, label, name, onDeleteFile, sectionAlias, subSection, subSectionResponses } =
    props;
  const itemsLength = subSectionResponses?.length || 0;
  // openSubSectionId: subSectionId | '': create | undefined: close
  const [openSubSectionId, setOpenSubSectionId] = React.useState<string | undefined>(undefined);
  const { t } = useTranslation('default');

  const handleOpen = (id: string) => {
    setOpenSubSectionId(id);
  };

  const handleClose = () => {
    setOpenSubSectionId(undefined);
  };

  const getFirstSubSectionResponse = (
    array?: SubSectionResponseDto[],
  ): SubSectionResponseDto | undefined => {
    return (array || []).find((s) => s.id === openSubSectionId);
  };

  const getSubSectionResponseName = (): string => {
    if (!openSubSectionId) return 'Create';
    const subSectionResponse = (subSectionResponses || []).find((s) => s.id === openSubSectionId);
    return subSectionResponse?.name ?? 'Unknown';
  };

  async function handleSave(questionResponses: QuestionResponseDto[]) {
    const { onSubmitSubSectionResponse } = props;
    const response = await (onSubmitSubSectionResponse &&
      onSubmitSubSectionResponse(name, questionResponses, openSubSectionId ?? undefined));
    setOpenSubSectionId(undefined);
    return response;
  }

  function handleDeleteSubSectionResponse(
    e: React.MouseEvent<HTMLElement>,
    subSectionResponseId: string,
  ) {
    e.stopPropagation();
    // DeleteSubSectionResponse
    const { onDeleteSubSectionResponse } = props;
    if (onDeleteSubSectionResponse && subSection?.alias) {
      onDeleteSubSectionResponse(subSection.alias, subSectionResponseId);
    }
  }

  const renderList = (directorOrShareholder: SubSectionResponseDto[], subHeader: string) => {
    return (
      <div>
        {subHeader}
        <List dense={false}>
          {directorOrShareholder.map((r) => (
            <ListItem key={r.id}>
              <ListItemText primary={`${r.name ?? ''} - (${r.state ?? ''})`} />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleOpen(r.id || '')}
                  size="large"
                >
                  <Avatar>
                    <EditIcon />
                  </Avatar>
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={(e) => handleDeleteSubSectionResponse(e, r.id || '')}
                  size="large"
                >
                  <Avatar>
                    <DeleteIcon />
                  </Avatar>
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {!itemsMax ||
            (itemsLength < itemsMax && (
              <ListItem>
                <ListItemText primary={t('add-a-new-answer')} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleOpen('')}
                    size="large"
                  >
                    <Avatar>
                      <AddIcon />
                    </Avatar>
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </div>
    );
  };

  const directorList = (subSectionResponses || []).filter((record) => {
    const isDirectorResponse = record?.responses?.find(
      (r) => r.questionAlias === 'director-or-shareholder-is-director',
    );
    return get(isDirectorResponse, 'value');
  });
  const shareholderList = (subSectionResponses || []).filter((record) => {
    const isShareholderResponse = record?.responses?.find(
      (r) => r.questionAlias === 'director-or-shareholder-is-shareholder',
    );
    return get(isShareholderResponse, 'value');
  });

  const neither = (subSectionResponses || []).filter((record) => {
    const isDirectorResponse = record?.responses?.find(
      (r) => r.questionAlias === 'director-or-shareholder-is-director',
    );
    const isShareholderResponse = record?.responses?.find(
      (r) => r.questionAlias === 'director-or-shareholder-is-shareholder',
    );
    return !get(isDirectorResponse, 'value') && !get(isShareholderResponse, 'value');
  });

  return (
    <Root className={classes.root}>
      <div className={classes.label}>{label}</div>
      {renderList(directorList, 'Directors')}
      {renderList(shareholderList, 'Shareholders')}
      {neither.length > 0 && renderList(neither, 'Others')}
      <UpdateSubSectionDialog
        label={getSubSectionResponseName()}
        subSectionId={openSubSectionId}
        sectionAlias={sectionAlias}
        subSectionAlias={name}
        open={openSubSectionId !== undefined}
        subSection={subSection}
        onUpdateSection={props.onUpdateSection}
        subSectionResponse={getFirstSubSectionResponse(subSectionResponses)}
        onDeleteFile={onDeleteFile}
        onClose={handleClose}
        onSave={handleSave}
      />
    </Root>
  );
}

export default SubQuestionnaireForDirectorOrShareholder;
